import type { LandingFilters } from '@/hooks/use-landing-filters';
import type { SavedTemplate } from '@/types/saved-template';

/**
 * Pure search, site filter and sort applied to the list returned by
 * `useTemplates` (A1). Status filtering happens server-side via
 * `useTemplates(statuses)`, so it is not applied here. Runs in the consumer
 * (`routes/home.tsx`) so server-side pagination isn't a prerequisite
 * (A10 / D40 — pagination deferred). Sorts in place on a copy of the array;
 * never mutates the input.
 */
export function applyFiltersAndSort(
  templates: SavedTemplate[],
  // `pageSize` is omitted deliberately — paging is applied by the consumer
  // after filtering/sorting, so this pure function has no business reading it.
  // `statuses` is omitted because the server has already filtered by status.
  filters: Omit<LandingFilters, 'pageSize' | 'statuses'>,
): SavedTemplate[] {
  let result = templates;

  if (filters.search) {
    const needle = filters.search.toLowerCase();
    result = result.filter((t) => t.name.toLowerCase().includes(needle));
  }

  if (filters.sites.length > 0) {
    result = result.filter((t) =>
      (t.scope.sites ?? []).some((site) => filters.sites.includes(site.id)),
    );
  }

  // `.sort` mutates; copy first so the caller's array stays untouched.
  const sorted = [...result];
  switch (filters.sortBy) {
    case 'alphabetical':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'recentlyCreated':
      sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      break;
    case 'lastUsed':
      // `lastUsed` isn't on `SavedTemplate` today; fall back to modifiedAt.
      sorted.sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt));
      break;
    case 'recentlyModified':
    default:
      sorted.sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt));
  }

  return sorted;
}
